import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Reveal } from '../components/ui';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { useStatus } from '../context/StatusContext';

const Outage: React.FC = () => {
  const { publicHubStatusEnabled, outagePageActive } = useStatus();
  if (!publicHubStatusEnabled || !outagePageActive) {
    return <Navigate to="/" replace />;
  }
  return (
  <section className="page-shell page-section-major">
    <div className="page-inner">
    <Reveal dir="up">
      <div className="rounded-2xl p-8 sm:p-10 border border-warning-800/50 bg-secondary-900 overflow-hidden">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-warning-500/15 border border-warning-500/30 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-warning-400" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-warning-300">Service Outage</h1>
        </div>
        <p className="text-secondary-400 mb-6 text-lg">
          Some services are currently unavailable. We are working to restore them as soon as possible.
        </p>
        <OutageContent />
        <div className="mt-8 pt-6 border-t border-secondary-700/50">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </Reveal>
    </div>
  </section>
  );
};

/** Renders outage info from API; placeholder when no data. */
function OutageContent() {
  const { outageInfo: info, loading } = useStatus();

  if (loading) {
    return (
      <div className="text-secondary-500 text-sm">Loading outage details…</div>
    );
  }

  if (!info || (!info.expectedReturn && !info.type && !info.servicesImpacted?.length && !info.dataLossRisks && !info.securityRisks)) {
    return (
      <p className="text-secondary-400 text-sm">No additional details available at this time. Check back shortly.</p>
    );
  }

  return (
    <div className="space-y-4 text-secondary-400">
      {info.expectedReturn && (
        <p><span className="text-secondary-500 font-medium">Expected return:</span> {info.expectedReturn}</p>
      )}
      {info.type && (
        <p><span className="text-secondary-500 font-medium">Type:</span> {info.type}</p>
      )}
      {info.servicesImpacted && info.servicesImpacted.length > 0 && (
        <p>
          <span className="text-secondary-500 font-medium">Services impacted:</span>{' '}
          {info.servicesImpacted.join(', ')}
        </p>
      )}
      {info.dataLossRisks && (
        <p><span className="text-secondary-500 font-medium">Data / security:</span> {info.dataLossRisks}</p>
      )}
      {info.securityRisks && (
        <p><span className="text-secondary-500 font-medium">Security risks:</span> {info.securityRisks}</p>
      )}
    </div>
  );
}

export default Outage;
